/**
 * TenantAdminModal — CRUD interface for knowledge-base tenants.
 * Opened via the discrete ⚙ icon in the header.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { reports } from '@/api/client'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Tenant {
  name:       string
  label:      string
  has_prompt: boolean
  has_vs:     boolean
}

interface TenantForm {
  name:           string
  label:          string
  prompt_id:      string
  vs_id:          string
  prompt_version: string
}

const EMPTY_FORM: TenantForm = {
  name: '', label: '', prompt_id: '', vs_id: '', prompt_version: 'latest',
}

interface Props {
  onClose: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TenantAdminModal({ onClose }: Props) {
  const [tenants,  setTenants]  = useState<Tenant[]>([])
  const [loading,  setLoading]  = useState(true)
  const [form,     setForm]     = useState<TenantForm | null>(null)
  const [editName, setEditName] = useState<string | null>(null)  // null = new
  const [saving,   setSaving]   = useState(false)
  const [error,    setError]    = useState('')
  const backdropRef             = useRef<HTMLDivElement>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try { setTenants(await reports.getTenants()) } catch {}
    setLoading(false)
  }, [])

  useEffect(() => { reload() }, [reload])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const startAdd = () => {
    setEditName(null)
    setForm({ ...EMPTY_FORM })
    setError('')
  }

  const startEdit = (t: Tenant) => {
    setEditName(t.name)
    setForm({ name: t.name, label: t.label, prompt_id: '', vs_id: '', prompt_version: 'latest' })
    setError('')
  }

  const cancelForm = () => { setForm(null); setEditName(null); setError('') }

  const setField = (field: keyof TenantForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => f ? { ...f, [field]: e.target.value } : f)

  const save = async () => {
    if (!form) return
    setError('')
    if (!editName && !form.name.trim()) { setError('Name is required'); return }
    setSaving(true)
    try {
      if (editName) {
        await reports.updateTenant(editName, form)
      } else {
        await reports.createTenant(form)
      }
      setForm(null)
      setEditName(null)
      await reload()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
    setSaving(false)
  }

  const remove = async (name: string) => {
    if (!confirm(`Delete tenant "${name}"? This cannot be undone.`)) return
    try {
      await reports.deleteTenant(name)
      await reload()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === backdropRef.current) onClose() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Knowledge Base Tenants</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Configure OpenAI stored prompts &amp; vector stores for KB mode
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400
                       hover:text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-slate-400 text-center py-12">Loading tenants…</p>
          ) : (
            <div className="p-6 space-y-5">

              {/* Tenant table */}
              {tenants.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">
                  No tenants configured yet. Add one below.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-100">
                      <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Name</th>
                      <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">Label</th>
                      <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 uppercase tracking-wide text-center">Prompt</th>
                      <th className="pb-2 pr-3 text-xs font-semibold text-slate-400 uppercase tracking-wide text-center">VS</th>
                      <th className="pb-2 w-24"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr
                        key={t.name}
                        className={`border-b border-slate-50 group transition-colors
                          ${editName === t.name ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                      >
                        <td className="py-2.5 pr-3 font-mono text-xs text-slate-500">{t.name}</td>
                        <td className="py-2.5 pr-3 text-slate-700 font-medium">{t.label}</td>
                        <td className="py-2.5 pr-3 text-center text-base leading-none">
                          {t.has_prompt ? '✅' : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 pr-3 text-center text-base leading-none">
                          {t.has_vs ? '✅' : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                        <td className="py-2.5 text-right">
                          {t.name !== 'default' && (
                            <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end gap-3">
                              <button
                                onClick={() => startEdit(t)}
                                className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => remove(t.name)}
                                className="text-xs text-red-400 hover:text-red-600 font-medium"
                              >
                                Delete
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Inline form — shown when adding or editing */}
              {form && (
                <div className="border border-slate-200 rounded-xl p-5 bg-slate-50 space-y-4">
                  <h3 className="text-sm font-semibold text-slate-700">
                    {editName ? `Edit "${editName}"` : 'New tenant'}
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Name */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Name <span className="text-slate-300">(slug, lowercase)</span>
                      </label>
                      <input
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5
                                   focus:outline-none focus:ring-2 focus:ring-blue-400
                                   disabled:bg-slate-100 disabled:text-slate-400"
                        value={form.name}
                        disabled={!!editName}
                        onChange={e =>
                          setForm(f => f
                            ? { ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }
                            : f)
                        }
                        placeholder="my_tenant"
                        autoFocus={!editName}
                      />
                    </div>

                    {/* Label */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Label</label>
                      <input
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5
                                   focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={form.label}
                        onChange={setField('label')}
                        placeholder="My Tenant"
                      />
                    </div>

                    {/* Prompt ID */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Prompt ID <span className="text-slate-300">(pmpt_…)</span>
                      </label>
                      <input
                        className="w-full text-sm font-mono border border-slate-200 rounded-lg px-3 py-1.5
                                   focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={form.prompt_id}
                        onChange={setField('prompt_id')}
                        placeholder={editName ? 'leave blank to keep existing' : 'pmpt_…'}
                      />
                    </div>

                    {/* Vector Store ID */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Vector Store ID <span className="text-slate-300">(vs_…)</span>
                      </label>
                      <input
                        className="w-full text-sm font-mono border border-slate-200 rounded-lg px-3 py-1.5
                                   focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={form.vs_id}
                        onChange={setField('vs_id')}
                        placeholder={editName ? 'leave blank to keep existing' : 'vs_…'}
                      />
                    </div>

                    {/* Prompt version */}
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">
                        Prompt version <span className="text-slate-300">(default: latest)</span>
                      </label>
                      <input
                        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-1.5
                                   focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={form.prompt_version}
                        onChange={setField('prompt_version')}
                        placeholder="latest"
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="text-xs text-red-500 flex items-center gap-1.5">
                      <span>⚠</span> {error}
                    </p>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={save}
                      disabled={saving}
                      className="px-4 py-1.5 text-sm font-medium bg-slate-900 text-white rounded-lg
                                 hover:bg-slate-700 disabled:opacity-50 transition-colors"
                    >
                      {saving ? 'Saving…' : editName ? 'Update' : 'Add tenant'}
                    </button>
                    <button
                      onClick={cancelForm}
                      className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        {!form && !loading && (
          <div className="px-6 py-3 border-t border-slate-100 flex-shrink-0">
            <button
              onClick={startAdd}
              className="flex items-center gap-1.5 text-sm font-medium text-blue-600
                         hover:text-blue-800 transition-colors"
            >
              <span className="text-lg leading-none">+</span>
              Add tenant
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
